import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../utils/apiBase';
import { RatingChip } from './rating-chip';

export type PlayerLite = {
  id: number;
  firstName: string;
  lastName: string;
  distance?: number;
  singlesRating?: number | null;
  doublesRating?: number | null;
};

type RatingKind = 'singles' | 'doubles' | 'none';

interface Props {
  onSelect: (player: PlayerLite | { newPlayer: true; query: string }) => void;
  placeholder?: string;
  fetchPlayers?: (query: string) => Promise<PlayerLite[]>;
  /** Which rating to display next to the player name. */
  ratingKind?: RatingKind;
  /** Disable input + dropdown while a parent action is in-flight. */
  disabled?: boolean;
  /** Hide the "+ Utwórz nowego" affordance when the parent only wants to pick existing players. */
  allowCreate?: boolean;
  createLabel?: string;
}

function initials(first: string | undefined, last: string | undefined): string {
  const f = (first ?? '').trim().charAt(0).toUpperCase();
  const l = (last ?? '').trim().charAt(0).toUpperCase();
  if (f && l) {
    return `${f}${l}`;
  }

  return (f || l || '?').repeat(2).slice(0, 2);
}

export function PlayerAutocomplete({
  onSelect,
  placeholder = 'Szukaj zawodnika…',
  fetchPlayers,
  ratingKind = 'singles',
  disabled = false,
  allowCreate = true,
  createLabel = 'Utwórz nowego',
}: Props): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlayerLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const fetchFn = fetchPlayers ?? defaultFetch;

  // Debounced fetch.
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const items = await fetchFn(query.trim());
        setResults(items);
        setActiveIdx(0);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [query, fetchFn]);

  // Click outside → close.
  useEffect(() => {
    function onDocPointer(e: MouseEvent): void {
      if (!rootRef.current) {
        return;
      }
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocPointer);

    return () => document.removeEventListener('mousedown', onDocPointer);
  }, []);

  const showCreate = allowCreate && query.trim().length >= 2 && results.length === 0 && !loading;
  const optionCount = results.length + (showCreate ? 1 : 0);

  const select = useCallback(
    (selection: PlayerLite | { newPlayer: true; query: string }): void => {
      setQuery('');
      setResults([]);
      setOpen(false);
      setActiveIdx(0);
      onSelect(selection);
    },
    [onSelect]
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setOpen(false);

      return;
    }
    if (!open || optionCount === 0) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % optionCount);

      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + optionCount) % optionCount);

      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx < results.length) {
        select(results[activeIdx]);
      } else if (showCreate) {
        select({ newPlayer: true, query: query.trim() });
      }
    }
  }

  const hasInput = query.trim().length >= 2;

  return (
    <div
      className={`pac${disabled ? ' is-disabled' : ''}${open && hasInput ? ' is-open' : ''}`}
      ref={rootRef}
    >
      <div className="pac__input-wrap">
        <span className="pac__icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          className="pac__input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Szukaj zawodnika"
          aria-autocomplete="list"
          aria-expanded={open && hasInput}
          aria-controls="pac-listbox"
          aria-activedescendant={
            open && optionCount > 0
              ? activeIdx < results.length
                ? `pac-opt-${results[activeIdx].id}`
                : 'pac-opt-create'
              : undefined
          }
          disabled={disabled}
          role="combobox"
          name="player-search"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            className="pac__clear"
            onClick={() => {
              setQuery('');
              setResults([]);
              inputRef.current?.focus();
            }}
            aria-label="Wyczyść"
          >
            ×
          </button>
        )}
        {loading && <span className="pac__spinner" aria-hidden="true" />}
      </div>

      {open && hasInput && (
        <div className="pac__panel" role="listbox" id="pac-listbox">
          {results.length === 0 && !loading && !showCreate && (
            <p className="pac__empty">Brak wyników dla „{query.trim()}".</p>
          )}

          {results.length > 0 && (
            <ul className="pac__list">
              {results.map((p, idx) => (
                <PlayerRow
                  key={p.id}
                  player={p}
                  ratingKind={ratingKind}
                  active={idx === activeIdx}
                  onClick={() => select(p)}
                  onMouseEnter={() => setActiveIdx(idx)}
                />
              ))}
            </ul>
          )}

          {showCreate && (
            <button
              id="pac-opt-create"
              type="button"
              className={`pac__create${activeIdx === results.length ? ' is-active' : ''}`}
              onClick={() => select({ newPlayer: true, query: query.trim() })}
              onMouseEnter={() => setActiveIdx(results.length)}
              role="option"
              aria-selected={activeIdx === results.length}
            >
              <span className="pac__create-plus" aria-hidden="true">+</span>
              <span>
                {createLabel}: <strong>„{query.trim()}"</strong>
              </span>
            </button>
          )}

          <div className="pac__hint">
            <kbd>↑↓</kbd> nawigacja · <kbd>Enter</kbd> wybierz · <kbd>Esc</kbd> zamknij
          </div>
        </div>
      )}
    </div>
  );
}

interface PlayerRowProps {
  player: PlayerLite;
  ratingKind: RatingKind;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function PlayerRow({ player, ratingKind, active, onClick, onMouseEnter }: PlayerRowProps): JSX.Element {
  const rating =
    ratingKind === 'doubles'
      ? player.doublesRating
      : ratingKind === 'singles'
        ? player.singlesRating
        : null;

  // For ratingKind 'none' we still want to surface both rankings if present —
  // gives the admin context about the player without picking a side.
  const showBoth = ratingKind === 'none';
  const bothS = player.singlesRating;
  const bothD = player.doublesRating;

  return (
    <li
      id={`pac-opt-${player.id}`}
      role="option"
      aria-selected={active}
      className={`pac__row${active ? ' is-active' : ''}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <span className="pac__row-bar" aria-hidden="true" />
      <span className="pac__monogram" aria-hidden="true">
        {initials(player.firstName, player.lastName)}
      </span>
      <span className="pac__name">
        <span className="pac__first">{player.firstName}</span>
        <span className="pac__last">{player.lastName}</span>
      </span>
      <span className="pac__rating">
        {showBoth ? (
          <span className="pac__rating-pair">
            <RatingChip tag="S" value={bothS} />
            <RatingChip tag="D" value={bothD} />
          </span>
        ) : (
          <RatingChip tag={ratingKind === 'doubles' ? 'D' : 'S'} value={rating} prominent />
        )}
      </span>
    </li>
  );
}

// The viewer has no admin session: /api/players is the organiser's route and
// answers 401 here, which silently emptied every dropdown. The public player
// base is the one a logged-in player may search.
async function defaultFetch(query: string): Promise<PlayerLite[]> {
  const res = await fetch(apiUrl(`/api/public/players?search=${encodeURIComponent(query)}`));
  if (!res.ok) {
    return [];
  }
  const data = (await res.json()) as { items?: PlayerLite[] };

  return data.items ?? [];
}
